import { isNumber, round } from "lodash-es";
import { type FC } from "react";

import { PointsEditor } from "components/materials/common/PointsEditor";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

interface AssessmentPointsProps {
  disabled?: boolean;
  onSave: (points: number) => void;
  page: PageType;
  points: number | null | undefined;
}

export const AssessmentPoints: FC<AssessmentPointsProps> = ({
  disabled = false,
  onSave,
  page,
  points,
}) => {
  // This component isn't supposed to be rendered at all if points is null, but check anyway.
  if (!isNumber(points)) {
    console.warn("Unexpected rendering of AssessmentPoints with null or undefined points prop");
    return null;
  }

  const buttonLabel = t("rubric_card.current_assessment_points", {
    current: points,
    max: page.max_points,
  });

  const parseInput = (input: string) => {
    // Empty input is not valid, but also not an error.
    if (!input) {
      return {};
    }

    // Validate format and range.
    const value = parseFloat(input);
    if (isNaN(value) || value < 0 || value > page.max_points) {
      return {
        error: t("submission_assessment.invalid_point_value", {
          max: page.max_points,
        }),
      };
    }

    // After passing the range validation check the precision limit.
    if ((value * 10) % 1 !== 0) {
      return {
        error: t("submission_assessment.invalid_point_precision"),
      };
    }

    return { value };
  };

  const onSavePoints = (newPoints: number) => {
    const pointsDelta = round(newPoints - points, 2);
    trackEvent("Assessment - Update points", {
      ...BUTTON_EVENT_CATEGORY,
      pageId: page.id,
      pageTitle: page.title,
      points_delta: pointsDelta,
      page_max_points: page.max_points,
      override_direction: pointsDelta > 0,
    });

    onSave(newPoints);
  };

  return (
    <PointsEditor
      buttonLabel={buttonLabel}
      disabled={disabled}
      initialPoints={points}
      inputMax={page.max_points}
      inputStep="0.1"
      inputTitle={t("tooltip.modify_points")}
      page={page}
      parseInput={parseInput}
      saveTextKey="update"
      tooltipText={t("tooltip.modify_points")}
      onSave={onSavePoints}
    />
  );
};
